import { useEffect, useLayoutEffect, useState } from "react";

function useDebouncedValue(value, delay = 700) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timeout);
  }, [value, delay]);

  return debouncedValue;
}

export default useDebouncedValue;

/**
 * Keeps text entry separate from the query that is allowed to reach an API.
 * Short input is held locally, while clearing the field resets immediately.
 */
export function useDebouncedSearch(initialValue = "", options = {}) {
  const { delay = 700, minLength = 2 } = options;
  const [inputValue, setInputValue] = useState(initialValue);
  const [query, setQuery] = useState(initialValue);
  const [isPending, setIsPending] = useState(false);
  const normalizedInput = inputValue.trim();
  const isSearchable = normalizedInput.length >= minLength;

  // Run before consumers' passive fetch effects so raw keystrokes can never
  // trigger a request with the previous committed query.
  useLayoutEffect(() => {
    if (!normalizedInput) {
      setQuery("");
    }
    setIsPending(Boolean(normalizedInput));
  }, [inputValue, normalizedInput]);

  useEffect(() => {
    if (!normalizedInput) {
      setQuery("");
      setIsPending(false);
      return undefined;
    }

    const timeout = setTimeout(() => {
      setQuery(isSearchable ? inputValue : "");
      setIsPending(false);
    }, delay);

    return () => clearTimeout(timeout);
  }, [delay, inputValue, isSearchable, normalizedInput]);

  return { inputValue, setInputValue, query, isPending, isSearchable };
}
